import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Alert,
  Radio,
  Upload,
  message,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, DownloadOutlined, FileTextOutlined, PlusOutlined, SaveOutlined, PrinterOutlined, SwapOutlined, UploadOutlined, SyncOutlined } from '@ant-design/icons';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import { canDeleteTradingRentalEstimation } from '../../utils/documentDeletePolicy';
import dayjs from 'dayjs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useOpportunity, useOpportunities } from '@/hooks/useOpportunities';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { useItems } from '@/hooks/useInventory';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  useConvertTradingRentalToQuotation,
  useCreateTradingRentalEstimation,
  useDeleteTradingRentalEstimation,
  useImportTradingRentalLines,
  useTradingRentalEstimation,
  useTradingRentalRelatedQuotations,
  useUpdateTradingRentalEstimation,
} from '@/hooks/useTradingRentalEstimation';
import type {
  TradingRentalEstimation,
  TradingRentalEstimationLine,
  TradingRentalEstimationOverhead,
  TradingRentalLineImportMode,
  TradingRentalLineImportPreview,
  TradingRentalLineType,
} from '@/services/tradingRentalEstimation.service';
import { useAuthStore } from '@/store/authStore';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_ESTIMATION } from '@/constants/approval-entity-types';
import tradingRentalEstimationService from '@/services/tradingRentalEstimation.service';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import { documentKeys } from '@/hooks/useDocuments';
import { DOCUMENT_ENTITY_TYPE_TRADING_RENTAL_ESTIMATION_LINE } from '@/constants/document-entity-types';
import { ProjectSelector } from '@/components/project';
import { useProjectsForSelection } from '@/hooks/useProject';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { projectService } from '@/services/project.service';
import type { UploadFile } from 'antd/es/upload/interface';
import { buildTradingRentalEstimationPrintHtml } from '@/utils/tradingRentalEstimationPrintHtml';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

function asNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function deriveRentalDuration(line: Partial<TradingRentalEstimationLine>): number {
  const explicit = asNum(line.rental_duration);
  if (explicit > 0) return explicit;
  if (!line.rental_start_date || !line.rental_end_date) return 0;
  const start = dayjs(line.rental_start_date);
  const end = dayjs(line.rental_end_date);
  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) return 0;
  const days = end.diff(start, 'day') + 1;
  const period = String(line.rental_period_type || 'Daily').toLowerCase();
  if (period === 'weekly') return Math.max(1, Math.ceil(days / 7));
  if (period === 'monthly') return Math.max(1, Math.ceil(days / 30));
  return Math.max(1, days);
}

function calculateDraftLine(line: Partial<TradingRentalEstimationLine>): Partial<TradingRentalEstimationLine> {
  const lineType = String(line.line_type || 'Trading').toLowerCase();
  const quantity = asNum(line.quantity);
  const discountPct = asNum(line.discount_percentage);
  const taxPct = asNum(line.tax_percentage);
  const out: Partial<TradingRentalEstimationLine> = { ...line };

  if (lineType === 'rental') {
    const rentalDuration = deriveRentalDuration(out);
    const rentalRate = asNum(out.rental_rate);
    const base = quantity * rentalDuration * rentalRate;
    const extras =
      asNum(out.security_deposit) +
      asNum(out.mobilization_charges) +
      asNum(out.demobilization_charges) +
      asNum(out.operator_charges) +
      asNum(out.transport_charges) +
      asNum(out.damage_charges);
    const beforeDiscount = base + extras;
    const discountAmount = (beforeDiscount * discountPct) / 100;
    const beforeTax = beforeDiscount - discountAmount;
    const taxAmount = (beforeTax * taxPct) / 100;
    const costAmount = quantity * rentalDuration * asNum(out.unit_cost);
    out.rental_duration = rentalDuration;
    out.cost_amount = costAmount;
    out.margin_amount = costAmount > 0 ? beforeTax - costAmount : 0;
    out.selling_amount = beforeTax;
    out.tax_amount = taxAmount;
    out.total_amount = beforeTax + taxAmount;
    return out;
  }

  const unitCost = asNum(out.unit_cost);
  const marginPct = asNum(out.margin_percentage);
  const markupPct = asNum(out.markup_percentage);
  let unitSelling = asNum(out.selling_unit_price);
  if (unitSelling <= 0) {
    unitSelling = unitCost * (1 + (markupPct > 0 ? markupPct : marginPct) / 100);
  }
  const grossSelling = quantity * unitSelling;
  const discountAmount = (grossSelling * discountPct) / 100;
  const beforeTax = grossSelling - discountAmount;
  const taxAmount = (beforeTax * taxPct) / 100;
  out.unit_cost = unitCost;
  out.selling_unit_price = unitSelling;
  out.cost_amount = quantity * unitCost;
  out.margin_amount = beforeTax - quantity * unitCost;
  out.selling_amount = beforeTax;
  out.tax_amount = taxAmount;
  out.total_amount = beforeTax + taxAmount;
  return out;
}

function roundPct(v: number): number {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

const OVERHEAD_TYPE_OPTIONS = [
  'Freight',
  'Transportation',
  'Import Duty',
  'Customs Charges',
  'Clearing Charges',
  'Insurance',
  'Documentation Charges',
  'Warehouse Charges',
  'Mobilization',
  'Site Handling',
  'Administrative Overhead',
  'Project Overhead',
  'Rental Setup Cost',
  'Delivery Cost',
  'Other',
];

const TradingRentalEstimationBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const prefilledOpportunityId = searchParams.get('opportunity_id')?.trim() || undefined;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [lineForm] = Form.useForm();
  const { user } = useAuthStore();
  const { formatCurrency } = useCurrency();
  const [lines, setLines] = useState<Partial<TradingRentalEstimationLine>[]>([]);
  const [overheads, setOverheads] = useState<Partial<TradingRentalEstimationOverhead>[]>([]);
  const [lineModalOpen, setLineModalOpen] = useState(false);
  const [lineModalType, setLineModalType] = useState<TradingRentalLineType>('Trading');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [convertDepartmentId, setConvertDepartmentId] = useState<string | undefined>();
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const { data: inventoryItems = [], isLoading: inventoryItemsLoading } = useItems({ is_active: true });
  const [transferOpen, setTransferOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLineType, setImportLineType] = useState<TradingRentalLineType>('Trading');
  const [importMode, setImportMode] = useState<TradingRentalLineImportMode>('replace');
  const [importPreview, setImportPreview] = useState<TradingRentalLineImportPreview | null>(null);
  const [importUploadFile, setImportUploadFile] = useState<UploadFile | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [syncingOpportunityDocs, setSyncingOpportunityDocs] = useState(false);

  const { data: treRow, isLoading } = useTradingRentalEstimation(id || '', { enabled: !!id });
  const row: TradingRentalEstimation | undefined = treRow;
  const { data: relatedQuotations = [] } = useTradingRentalRelatedQuotations(id || '', { enabled: !!id });
  const { data: availabilitySummary } = useQuery({
    queryKey: ['trading-rental-estimations', 'availability-summary', id],
    queryFn: () => tradingRentalEstimationService.getAvailabilitySummary(id || ''),
    enabled: !!id,
  });
  const { data: customers = [] } = useActiveCustomers();
  const { data: opportunities = [] } = useOpportunities({ status: 'Open' });
  const opportunityId = Form.useWatch('opportunity_id', form);
  const customerId = Form.useWatch('customer_id', form);
  const { data: projectsForSelection = [] } = useProjectsForSelection();
  const { data: linkedOpportunity } = useOpportunity(opportunityId);
  const { data: prefilledOpportunity } = useOpportunity(
    !id ? prefilledOpportunityId : undefined
  );
  const { data: rowSourceOpportunity } = useOpportunity(row?.opportunity_id || undefined);
  const createMutation = useCreateTradingRentalEstimation();
  const updateMutation = useUpdateTradingRentalEstimation();
  const convertMutation = useConvertTradingRentalToQuotation();
  const deleteMutation = useDeleteTradingRentalEstimation();
  const importLinesMutation = useImportTradingRentalLines();

  const isEditable = !row || row.status !== 'Cancelled';

  const isSuperAdmin = Boolean(user?.is_super_admin);
  const perms = user?.permissions || [];
  const canDelete =
    isSuperAdmin || perms.includes('growth.trading_rental_estimation.delete') || perms.includes('growth.view');
  const canDeleteCurrent =
    !!row &&
    canDelete &&
    canDeleteTradingRentalEstimation({
      status: row.status,
      approval_status: row.approval_status,
      related_quotation_count: Number(row.related_quotation_count || relatedQuotations.length || 0),
    }).ok;
  const canViewCost = isSuperAdmin || perms.includes('growth.trading_rental_estimation.cost_margin.view') || perms.includes('growth.view');
  const canViewOverhead = isSuperAdmin || perms.includes('growth.overhead.view') || canViewCost;
  const canManageOverhead = isSuperAdmin || perms.includes('growth.overhead.manage') || canViewCost;
  const canViewProfitability = isSuperAdmin || perms.includes('growth.profitability.view') || perms.includes('growth.margin.view') || canViewCost;
  const canEditCost = canViewCost;
  const canConvert = isSuperAdmin || perms.includes('growth.trading_rental_estimation.convert') || perms.includes('crm.quotation.create') || perms.includes('growth.view');
  const linkedOpportunityId = row?.opportunity_id || opportunityId || undefined;
  const opportunityOptions = useMemo(() => {
    const base = opportunities.map((o: any) => ({
      value: o.id,
      label: `${o.opportunity_number} — ${o.opportunity_name}`,
    }));
    if (linkedOpportunity?.id && !base.some((o) => o.value === linkedOpportunity.id)) {
      base.push({
        value: linkedOpportunity.id,
        label: `${linkedOpportunity.opportunity_number} — ${linkedOpportunity.opportunity_name}`,
      });
    }
    return base;
  }, [opportunities, linkedOpportunity]);

  const pageTitle = useMemo(() => {
    if (!id) return 'Create Trading/Rental Estimation';
    if (isLoading && !row?.estimation_number) return 'Trading/Rental Estimation — Loading…';
    if (row?.estimation_number) return `Trading/Rental Estimation — ${row.estimation_number}`;
    return 'Trading/Rental Estimation';
  }, [id, isLoading, row?.estimation_number]);

  React.useEffect(() => {
    if (id || !prefilledOpportunity) return;
    const patch: Record<string, unknown> = {
      opportunity_id: prefilledOpportunity.id,
      customer_id: prefilledOpportunity.customer_id || undefined,
      customer_name: prefilledOpportunity.customer_name,
    };
    if (prefilledOpportunity.project_id) {
      const linked = projectsForSelection.find((p) => p.id === prefilledOpportunity.project_id);
      patch.projectId = prefilledOpportunity.project_id;
      patch.projectName = linked?.project_name;
    }
    form.setFieldsValue(patch);
  }, [id, prefilledOpportunity, form, projectsForSelection]);

  React.useEffect(() => {
    if (!row?.id) return;
    form.setFieldsValue({
      estimation_number: row.estimation_number,
      estimation_date: row.estimation_date ? dayjs(row.estimation_date) : dayjs(),
      estimation_type: row.estimation_type,
      opportunity_id: row.opportunity_id || undefined,
      customer_id: row.customer_id || undefined,
      customer_name: row.customer_name,
      contact_person: row.contact_person || undefined,
      projectId: row.project_id || undefined,
      projectName: row.project_name || undefined,
      currency: row.currency,
      price_validity_days: row.price_validity_days,
      expected_start_date: row.expected_start_date ? dayjs(row.expected_start_date) : undefined,
      remarks: row.remarks || undefined,
      internal_notes: row.internal_notes || undefined,
      terms_and_conditions: row.terms_and_conditions || undefined,
      status: row.status,
    });
    setLines(Array.isArray(row.lines) ? row.lines.map((line) => ({ ...line })) : []);
    setOverheads(Array.isArray(row.overheads) ? row.overheads.map((oh) => ({ ...oh })) : []);
  }, [row?.id, row?.estimation_number, row?.updated_at, form]);

  const lineTotals = useMemo(() => {
    const totalCost = lines.reduce((s, l) => s + Number(l.cost_amount || 0), 0);
    const totalMargin = lines.reduce((s, l) => s + Number(l.margin_amount || 0), 0);
    const totalBeforeTax = lines.reduce((s, l) => s + Number(l.selling_amount || 0), 0);
    const totalTax = lines.reduce((s, l) => s + Number(l.tax_amount || 0), 0);
    const grandTotal = lines.reduce((s, l) => s + Number(l.total_amount || 0), 0);
    return { totalCost, totalMargin, totalBeforeTax, totalTax, grandTotal };
  }, [lines]);

  const openAddLine = (lineType: TradingRentalLineType) => {
    setLineModalType(lineType);
    setEditingIndex(null);
    setEditingLineId(null);
    lineForm.resetFields();
    lineForm.setFieldsValue({ line_type: lineType, quantity: 1, tax_percentage: 5, discount_percentage: 0 });
    setLineModalOpen(true);
  };

  const syncMarginMarkup = (values?: Partial<TradingRentalEstimationLine>) => {
    const current = values || (lineForm.getFieldsValue(true) as Partial<TradingRentalEstimationLine>);
    const lineType = String(current.line_type || lineModalType || 'Trading').toLowerCase();
    if (lineType !== 'trading') return;
    const unitCost = asNum(current.unit_cost);
    const unitSelling = asNum(current.selling_unit_price);
    const markup = unitCost > 0 && unitSelling > 0 ? roundPct(((unitSelling - unitCost) / unitCost) * 100) : 0;
    const margin = unitSelling > 0 ? roundPct(((unitSelling - unitCost) / unitSelling) * 100) : 0;
    lineForm.setFieldsValue({
      margin_percentage: margin,
      markup_percentage: markup,
    });
  };

  const syncFromMargin = (values?: Partial<TradingRentalEstimationLine>) => {
    const current = values || (lineForm.getFieldsValue(true) as Partial<TradingRentalEstimationLine>);
    const lineType = String(current.line_type || lineModalType || 'Trading').toLowerCase();
    if (lineType !== 'trading') return;
    const unitCost = asNum(current.unit_cost);
    const margin = asNum(current.margin_percentage);
    if (unitCost <= 0 || margin >= 100) return;
    const unitSelling = roundPct(unitCost / (1 - margin / 100));
    const markup = unitCost > 0 ? roundPct(((unitSelling - unitCost) / unitCost) * 100) : 0;
    lineForm.setFieldsValue({
      selling_unit_price: unitSelling,
      markup_percentage: markup,
    });
  };

  const syncFromMarkup = (values?: Partial<TradingRentalEstimationLine>) => {
    const current = values || (lineForm.getFieldsValue(true) as Partial<TradingRentalEstimationLine>);
    const lineType = String(current.line_type || lineModalType || 'Trading').toLowerCase();
    if (lineType !== 'trading') return;
    const unitCost = asNum(current.unit_cost);
    const markup = asNum(current.markup_percentage);
    if (unitCost <= 0) return;
    const unitSelling = roundPct(unitCost * (1 + markup / 100));
    const margin = unitSelling > 0 ? roundPct(((unitSelling - unitCost) / unitSelling) * 100) : 0;
    lineForm.setFieldsValue({
      selling_unit_price: unitSelling,
      margin_percentage: margin,
    });
  };

  const openEditLine = (index: number) => {
    const line = lines[index];
    if (!line) return;
    setLineModalType((line.line_type as TradingRentalLineType) || 'Trading');
    setEditingIndex(index);
    lineForm.setFieldsValue({
      ...line,
      expected_delivery_date: line.expected_delivery_date ? dayjs(line.expected_delivery_date as any) : undefined,
      rental_start_date: line.rental_start_date ? dayjs(line.rental_start_date as any) : undefined,
      rental_end_date: line.rental_end_date ? dayjs(line.rental_end_date as any) : undefined,
    });
    syncMarginMarkup(line);
    setEditingLineId(String(line.id || ''));
    setLineModalOpen(true);
  };

  const onPickItem = (itemId: string) => {
    const item = inventoryItems.find((x) => x.id === itemId);
    if (!item) return;
    lineForm.setFieldsValue({
      item_code: item.item_code,
      item_name: item.item_name,
      item_description: item.item_description || item.item_name,
      item_category: item.item_type || '',
      brand: item.manufacturer || '',
      uom: item.sales_uom || item.base_uom || 'PCS',
      unit_cost: Number(item.average_cost || item.standard_cost || 0),
      selling_unit_price: Number(item.standard_selling_price || 0),
      tax_percentage: Number(item.vat_percentage || 5),
    });
    syncMarginMarkup({
      ...(lineForm.getFieldsValue(true) as Partial<TradingRentalEstimationLine>),
      unit_cost: Number(item.average_cost || item.standard_cost || 0),
      selling_unit_price: Number(item.standard_selling_price || 0),
    });
  };

  const saveLine = async () => {
    const values = await lineForm.validateFields();
    const next = [...lines];
    const rowData: Partial<TradingRentalEstimationLine> = {
      ...values,
      expected_delivery_date: values.expected_delivery_date ? dayjs(values.expected_delivery_date).format('YYYY-MM-DD') : undefined,
      rental_start_date: values.rental_start_date ? dayjs(values.rental_start_date).format('YYYY-MM-DD') : undefined,
      rental_end_date: values.rental_end_date ? dayjs(values.rental_end_date).format('YYYY-MM-DD') : undefined,
      line_number: editingIndex != null ? next[editingIndex]?.line_number || editingIndex + 1 : next.length + 1,
    };
    const lineType = String(rowData.line_type || 'Trading');
    if (lineType === 'Trading') {
      if (asNum(rowData.quantity) <= 0) {
        message.error('Trading line quantity must be greater than 0');
        return;
      }
      if (asNum(rowData.unit_cost) <= 0 && asNum(rowData.selling_unit_price) <= 0) {
        message.error('Provide unit cost or selling unit price for trading line');
        return;
      }
    } else {
      const rentalDuration = deriveRentalDuration(rowData);
      if (asNum(rowData.quantity) <= 0) {
        message.error('Rental line quantity must be greater than 0');
        return;
      }
      if (rentalDuration <= 0) {
        message.error('Provide rental duration or valid rental start/end dates');
        return;
      }
      if (asNum(rowData.rental_rate) <= 0) {
        message.error('Rental rate must be greater than 0');
        return;
      }
      rowData.rental_duration = rentalDuration;
    }
    const calculatedRow = calculateDraftLine(rowData);
    if (editingIndex != null) {
      next[editingIndex] = { ...next[editingIndex], ...calculatedRow };
    } else {
      next.push({ ...calculatedRow, id: `draft-${Date.now()}-${Math.random().toString(16).slice(2, 10)}` });
    }
    setLines(next);
    setLineModalOpen(false);
    const savedIndex = editingIndex != null ? editingIndex : next.length - 1;
    setEditingLineId(String(next[savedIndex]?.id || ''));
  };

  const onPrint = () => {
    const values = form.getFieldsValue(true);
    const customerName =
      customers.find((c: any) => c.id === (values.customer_id || row?.customer_id))?.customer_name ||
      values.customer_name ||
      row?.customer_name ||
      '';
    const printRow = {
      ...(row || {}),
      ...values,
      estimation_number: row?.estimation_number || 'Draft',
      estimation_date: values.estimation_date
        ? dayjs(values.estimation_date).format('YYYY-MM-DD')
        : row?.estimation_date,
      expected_start_date: values.expected_start_date
        ? dayjs(values.expected_start_date).format('YYYY-MM-DD')
        : row?.expected_start_date,
      customer_name: customerName,
      project_name: values.projectName ?? values.project_name ?? row?.project_name,
      project: row?.project,
      status: row?.status || 'Draft',
      approval_status: row?.approval_status || 'Not Required',
      currency: values.currency || row?.currency || 'AED',
      related_quotation_count: row?.related_quotation_count ?? relatedQuotations.length,
    };
    const html = buildTradingRentalEstimationPrintHtml(printRow, {
      lines,
      overheads,
      opportunity: linkedOpportunity || rowSourceOpportunity || null,
      canViewCost,
      canViewOverhead,
      canViewProfitability,
    });
    // Do not pass noopener — browsers return null and leave a blank tab.
    const win = window.open('', '_blank');
    if (!win) {
      message.error('Allow pop-ups to print');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    window.setTimeout(() => {
      try {
        win.print();
      } catch {
        // Ignore print dialog failures in restricted browsers/headless mode.
      }
    }, 250);
  };

  const submit = async () => {
    const values = await form.validateFields();
    if (lines.length === 0) {
      message.warning('Add at least one line');
      return;
    }
    const { projectId, projectName, ...rest } = values;
    const payload: any = {
      ...rest,
      project_id: projectId || null,
      project_name: projectName || null,
      estimation_date: values.estimation_date?.format('YYYY-MM-DD'),
      expected_start_date: values.expected_start_date ? values.expected_start_date.format('YYYY-MM-DD') : undefined,
      lines,
      overheads: canManageOverhead ? overheads : undefined,
      customer_name:
        customers.find((c: any) => c.id === values.customer_id)?.customer_name ||
        values.customer_name,
    };
    try {
      if (id) {
        await updateMutation.mutateAsync({ id, data: payload });
      } else {
        const created = await createMutation.mutateAsync(payload);
        navigate(`/growth/estimations/trading-rental/${created.id}`);
      }
    } catch {
      // message from hook
    }
  };

  const openConvertModal = async () => {
    const allIds = lines.map((l, idx) => String(l.id || `tmp-${idx}`));
    setSelectedLineIds(allIds);
    setConvertDepartmentId(undefined);
    setConvertModalOpen(true);
    const projectId = row?.project_id ? String(row.project_id).trim() : '';
    if (projectId) {
      try {
        const project = await projectService.getProjectById(projectId);
        const deptId = project?.department_id ? String(project.department_id).trim() : '';
        if (deptId) setConvertDepartmentId(deptId);
      } catch {
        // Prefill is best-effort
      }
    }
  };

  const convertToQuotation = async () => {
    if (!id) return;
    if (selectedLineIds.length === 0) {
      message.warning('Select at least one line');
      return;
    }
    if (!convertDepartmentId?.trim()) {
      message.warning('Department is required');
      return;
    }
    const resp = await convertMutation.mutateAsync({
      id,
      selectedLineIds,
      departmentId: convertDepartmentId.trim(),
    });
    const quotationId = (resp as any)?.id || (resp as any)?.data?.id;
    setConvertModalOpen(false);
    if (quotationId) {
      navigate(`/growth/quotations/${quotationId}`);
    }
  };

  const handleDelete = () => {
    if (!id || !row) return;
    Modal.confirm({
      title: 'Delete estimation?',
      content: `Delete Trading/Rental estimation "${row.estimation_number}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await deleteMutation.mutateAsync(id);
        navigate('/growth/estimations/trading-rental');
      },
    });
  };

  const openImportModal = (lineType: TradingRentalLineType) => {
    setImportLineType(lineType);
    setImportMode('replace');
    setImportFile(null);
    setImportPreview(null);
    setImportUploadFile(null);
    setImportModalOpen(true);
  };

  const previewImportFile = async (file: File) => {
    setImportBusy(true);
    try {
      const preview = await tradingRentalEstimationService.previewImportLines(file, importLineType);
      setImportPreview(preview);
      if (preview.errors.length === 0 && preview.lines.length === 0) {
        message.warning('No data rows found in the file');
      }
    } catch (e: any) {
      setImportPreview({
        lines: [],
        errors: [e?.response?.data?.message || e?.message || 'Failed to read the file. Please use the downloaded template.'],
        warnings: [],
      });
    } finally {
      setImportBusy(false);
    }
  };

  const confirmImportLines = async () => {
    if (!importFile || !importPreview || importPreview.lines.length === 0 || importPreview.errors.length > 0) {
      message.error('Upload a valid file first');
      return;
    }
    setImportBusy(true);
    try {
      if (id) {
        const result = await importLinesMutation.mutateAsync({
          id,
          file: importFile,
          lineType: importLineType,
          mode: importMode,
        });
        setLines(Array.isArray(result.estimation.lines) ? result.estimation.lines.map((l) => ({ ...l })) : []);
      } else {
        const kept = importMode === 'replace' ? lines.filter((l) => l.line_type !== importLineType) : lines;
        const merged = [...kept, ...importPreview.lines.map((l) => calculateDraftLine({ ...l, line_type: importLineType }))];
        setLines(merged.map((l, i) => ({ ...l, line_number: i + 1 })));
        message.success(
          `Imported ${importPreview.lines.length} ${importLineType.toLowerCase()} line(s) — save the estimation to persist`
        );
      }
      setImportModalOpen(false);
    } finally {
      setImportBusy(false);
    }
  };

  const importUploadProps = {
    accept: '.xlsx,.xls,.csv',
    maxCount: 1,
    beforeUpload: (file: File) => {
      setImportFile(file);
      setImportUploadFile({ uid: '-1', name: file.name, status: 'done' } as UploadFile);
      void previewImportFile(file);
      return false;
    },
    onRemove: () => {
      setImportFile(null);
      setImportPreview(null);
      setImportUploadFile(null);
    },
    fileList: importUploadFile ? [importUploadFile] : [],
  };

  const columns = [
    { title: '#', dataIndex: 'line_number', key: 'line_number', width: 70 },
    {
      title: 'Type',
      dataIndex: 'line_type',
      render: (v: string) => <Tag color={v === 'Rental' ? 'purple' : 'blue'}>{v}</Tag>,
    },
    { title: 'Item', dataIndex: 'item_name', key: 'item_name' },
    { title: 'Description', dataIndex: 'item_description', key: 'item_description' },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', align: 'right' as const },
    { title: 'UOM', dataIndex: 'uom', key: 'uom' },
    {
      title: 'Availability',
      dataIndex: 'availability_status',
      render: (v: string) => v || '—',
    },
    ...(canViewOverhead
      ? [
          {
            title: 'Base Cost',
            key: 'base_cost_amount',
            render: (_: unknown, r: any) => formatCurrency(Number(r.base_cost_amount ?? r.cost_amount ?? 0)),
          },
          {
            title: 'Allocated OH',
            key: 'allocated_overhead_amount',
            render: (_: unknown, r: any) => formatCurrency(Number(r.allocated_overhead_amount || 0)),
          },
          {
            title: 'Loaded Cost',
            key: 'loaded_cost_amount',
            render: (_: unknown, r: any) => formatCurrency(Number(r.loaded_cost_amount ?? r.cost_amount ?? 0)),
          },
        ]
      : canViewCost
        ? [
            {
              title: 'Cost',
              key: 'cost_amount',
              render: (_: unknown, r: any) => formatCurrency(Number(r.cost_amount || 0)),
            },
          ]
        : []),
    ...(canViewCost
      ? [
          {
            title: 'Margin %',
            key: 'margin_percentage',
            render: (_: unknown, r: any) => `${Number(r.margin_percentage || 0).toFixed(2)}%`,
          },
          {
            title: 'Margin',
            key: 'margin_amount',
            render: (_: unknown, r: any) => formatCurrency(Number(r.margin_amount || 0)),
          },
        ]
      : []),
    {
      title: 'Line Total',
      key: 'total_amount',
      align: 'right' as const,
      render: (_: unknown, r: any) => formatCurrency(Number(r.selling_amount || 0)),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, _row: any, index: number) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditLine(index)}>
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
          >
            Remove
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card loading={isLoading}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row justify="space-between" align="middle">
            <Col>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/estimations/trading-rental')} style={{ marginBottom: 12 }}>
                Back to list
              </Button>
              <h2 style={{ margin: 0 }}>{pageTitle}</h2>
              {id && row?.sales_team_name ? (
                <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>Sales team: {row.sales_team_name}</div>
              ) : null}
            </Col>
            <Col>
              <Space>
                {id && canConvert && (
                  <Button icon={<FileTextOutlined />} onClick={openConvertModal}>
                    Convert to Quotation
                  </Button>
                )}
                {id && (
                  <Button icon={<PrinterOutlined />} onClick={onPrint}>
                    Print / Export
                  </Button>
                )}
                {id && (
                  <ReportDocumentActions
                    reportKey="trading-rental-estimation"
                    params={{ id }}
                    documentId={id}
                    sendPermissions={['growth.trading_rental_estimation.edit', 'growth.view']}
                  />
                )}
                {id && (
                  <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
                    Transfer team
                  </Button>
                )}
                {id && canDelete && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                    disabled={!canDeleteCurrent}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                )}
                <Button type="primary" icon={<SaveOutlined />} onClick={submit} loading={createMutation.isPending || updateMutation.isPending}>
                  Save
                </Button>
              </Space>
            </Col>
          </Row>

          {id ? (
            <SalesTeamTransferModal
              open={transferOpen}
              onClose={() => setTransferOpen(false)}
              entityType="TradingRentalEstimation"
              entityId={id}
              entityLabel={row?.estimation_number}
              currentTeamName={row?.sales_team_name}
              onSuccess={() => queryClient.invalidateQueries()}
            />
          ) : null}
          {id ? <TransferHistoryPanel entityType="TradingRentalEstimation" entityId={id} /> : null}

          <Form form={form} layout="vertical">
            <Row gutter={12}>
              {id ? (
                <Col span={6}>
                  <Form.Item name="estimation_number" label="Estimation #">
                    <Input readOnly placeholder={isLoading ? 'Loading…' : '—'} />
                  </Form.Item>
                </Col>
              ) : null}
              <Col span={6}>
                <Form.Item name="estimation_date" label="Estimation date" initialValue={dayjs()} rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="estimation_type" label="Estimation type" initialValue="Industrial Trading" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { value: 'Industrial Trading', label: 'Industrial Trading' },
                      { value: 'Rental Services', label: 'Rental Services' },
                      { value: 'Mixed', label: 'Mixed' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="opportunity_id" label="Opportunity">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={opportunityOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="currency" label="Currency" initialValue="AED" rules={[{ required: true }]}>
                  <Select options={[{ value: 'AED', label: 'AED' }, { value: 'USD', label: 'USD' }, { value: 'EUR', label: 'EUR' }]} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="customer_id" label="Customer">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={customers.map((c: any) => ({
                      value: c.id,
                      label: c.customer_name,
                    }))}
                    onChange={() => {
                      form.setFieldsValue({ projectId: undefined, projectName: undefined });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="projectId"
                  label="Project/Site"
                  rules={[{ required: true, message: 'Project is required' }]}
                >
                  <ProjectSelector
                    customerId={customerId}
                    customerName={
                      customers.find((c: any) => c.id === customerId)?.customer_name
                    }
                    placeholder="Select or create project"
                    onChange={(projectId, projectData) => {
                      form.setFieldsValue({
                        projectId,
                        projectName: projectData?.project_name,
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item name="projectName" hidden>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="contact_person" label="Contact person">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={6}>
                <Form.Item name="price_validity_days" label="Price validity (days)" initialValue={30}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="expected_start_date" label="Expected delivery/rental start">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="remarks" label="Remarks">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="internal_notes" label="Internal notes">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="terms_and_conditions" label="Terms & conditions">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {linkedOpportunityId ? (
            <Card size="small" title="Linked opportunity">
              <Space>
                <Tag color="blue">{linkedOpportunity?.opportunity_number || linkedOpportunityId}</Tag>
                <span>{linkedOpportunity?.opportunity_name || 'Linked opportunity'}</span>
                <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/opportunities/${linkedOpportunityId}`)}>
                  Open opportunity
                </Button>
              </Space>
            </Card>
          ) : null}

          {id ? (
            <EntityAttachmentsCard
              entityType="TradingRentalEstimation"
              entityId={id}
              title="Estimation attachments"
              readOnly={false}
              queryKeyPrefix={['documents', 'trading-rental-estimation']}
              extra={
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  disabled={!linkedOpportunityId}
                  loading={syncingOpportunityDocs}
                  title={linkedOpportunityId ? undefined : 'Link an opportunity to sync documents'}
                  onClick={async () => {
                    setSyncingOpportunityDocs(true);
                    try {
                      const r = await tradingRentalEstimationService.syncOpportunityDocuments(id);
                      message.success(
                        r.copied > 0
                          ? `Copied ${r.copied} document(s) from the opportunity`
                          : 'No new documents to copy from the opportunity'
                      );
                      queryClient.invalidateQueries({
                        queryKey: ['documents', 'trading-rental-estimation', 'TradingRentalEstimation', id],
                      });
                      queryClient.invalidateQueries({
                        queryKey: documentKeys.byEntity('TradingRentalEstimation', id),
                      });
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || e?.message || 'Sync failed');
                    } finally {
                      setSyncingOpportunityDocs(false);
                    }
                  }}
                >
                  Sync documents from opportunity
                </Button>
              }
            />
          ) : null}
          {id ? (
            <InheritedAttachmentsCard
              trailDomain="sales"
              entityType="TradingRentalEstimation"
              entityId={id}
              queryKeyPrefix={['documents', 'trading-rental-estimation']}
              style={{ marginTop: 0 }}
            />
          ) : null}

          {canViewOverhead ? (
            <Card
              title="Overheads"
              extra={
                canManageOverhead ? (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setOverheads((prev) => [
                        ...prev,
                        {
                          id: `draft-oh-${Date.now()}`,
                          line_number: prev.length + 1,
                          overhead_type: 'Freight',
                          currency: form.getFieldValue('currency') || 'AED',
                          exchange_rate: 1,
                          original_amount: 0,
                          amount: 0,
                        },
                      ])
                    }
                  >
                    Add overhead
                  </Button>
                ) : null
              }
            >
              <Table
                size="small"
                pagination={false}
                rowKey={(r, idx) => String(r.id || idx)}
                dataSource={overheads}
                columns={[
                  {
                    title: '#',
                    width: 50,
                    render: (_: unknown, __: unknown, idx: number) => idx + 1,
                  },
                  {
                    title: 'Type',
                    dataIndex: 'overhead_type',
                    render: (v: string, r: Partial<TradingRentalEstimationOverhead>, idx: number) =>
                      canManageOverhead ? (
                        <Select
                          style={{ width: '100%' }}
                          value={v}
                          options={OVERHEAD_TYPE_OPTIONS.map((t) => ({ value: t, label: t }))}
                          onChange={(val) =>
                            setOverheads((prev) => prev.map((oh, i) => (i === idx ? { ...oh, overhead_type: val } : oh)))
                          }
                        />
                      ) : (
                        v
                      ),
                  },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    render: (v: string, _r: Partial<TradingRentalEstimationOverhead>, idx: number) =>
                      canManageOverhead ? (
                        <Input
                          value={v}
                          onChange={(e) =>
                            setOverheads((prev) => prev.map((oh, i) => (i === idx ? { ...oh, description: e.target.value } : oh)))
                          }
                        />
                      ) : (
                        v || '—'
                      ),
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'original_amount',
                    align: 'right' as const,
                    render: (v: number, r: Partial<TradingRentalEstimationOverhead>, idx: number) =>
                      canManageOverhead ? (
                        <InputNumber
                          min={0}
                          value={v}
                          onChange={(val) =>
                            setOverheads((prev) =>
                              prev.map((oh, i) =>
                                i === idx ? { ...oh, original_amount: Number(val || 0), amount: Number(val || 0) } : oh
                              )
                            )
                          }
                        />
                      ) : (
                        formatCurrency(Number(v || 0))
                      ),
                  },
                  {
                    title: 'Currency',
                    dataIndex: 'currency',
                    render: (v: string, _r: Partial<TradingRentalEstimationOverhead>, idx: number) =>
                      canManageOverhead ? (
                        <Input
                          style={{ width: 70 }}
                          value={v}
                          onChange={(e) =>
                            setOverheads((prev) => prev.map((oh, i) => (i === idx ? { ...oh, currency: e.target.value } : oh)))
                          }
                        />
                      ) : (
                        v
                      ),
                  },
                  {
                    title: 'Remarks',
                    dataIndex: 'remarks',
                    render: (v: string, _r: Partial<TradingRentalEstimationOverhead>, idx: number) =>
                      canManageOverhead ? (
                        <Input
                          value={v}
                          onChange={(e) =>
                            setOverheads((prev) => prev.map((oh, i) => (i === idx ? { ...oh, remarks: e.target.value } : oh)))
                          }
                        />
                      ) : (
                        v || '—'
                      ),
                  },
                  ...(canManageOverhead
                    ? [
                        {
                          title: '',
                          key: 'actions',
                          render: (_: unknown, __: unknown, idx: number) => (
                            <Button type="link" danger size="small" onClick={() => setOverheads((prev) => prev.filter((_, i) => i !== idx))}>
                              Remove
                            </Button>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
              <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                Overheads are allocated to trading lines only (BY COST VALUE). Rental lines are excluded.
              </div>
            </Card>
          ) : null}

          <Card
            title="Estimation lines"
            extra={
              <Space>
                <Button icon={<UploadOutlined />} onClick={() => openImportModal('Trading')} disabled={!isEditable}>
                  Import trading lines
                </Button>
                <Button icon={<UploadOutlined />} onClick={() => openImportModal('Rental')} disabled={!isEditable}>
                  Import rental lines
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => openAddLine('Trading')}>
                  Add trading line
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => openAddLine('Rental')}>
                  Add rental line
                </Button>
              </Space>
            }
          >
            <Table rowKey={(_, idx) => String(lines[idx!]?.id || idx)} dataSource={lines} columns={columns as any} pagination={false} />
          </Card>

          <Descriptions bordered size="small" column={3} title={canViewProfitability ? 'Profitability summary' : undefined}>
            {canViewOverhead && (
              <>
                <Descriptions.Item label="Material cost">
                  {formatCurrency(Number(row?.material_cost ?? lineTotals.totalCost))}
                </Descriptions.Item>
                <Descriptions.Item label="Rental cost">{formatCurrency(Number(row?.rental_cost || 0))}</Descriptions.Item>
                <Descriptions.Item label="Total overhead">{formatCurrency(Number(row?.total_overhead || 0))}</Descriptions.Item>
                <Descriptions.Item label="Loaded cost">{formatCurrency(Number(row?.loaded_total_cost ?? lineTotals.totalCost))}</Descriptions.Item>
              </>
            )}
            {canViewCost && !canViewOverhead && <Descriptions.Item label="Total cost">{formatCurrency(lineTotals.totalCost)}</Descriptions.Item>}
            {canViewCost && <Descriptions.Item label="Total margin">{formatCurrency(Number(row?.total_margin ?? lineTotals.totalMargin))}</Descriptions.Item>}
            {canViewProfitability && (
              <>
                <Descriptions.Item label="Gross profit">{formatCurrency(Number(row?.gross_profit ?? lineTotals.totalMargin))}</Descriptions.Item>
                <Descriptions.Item label="Gross profit %">
                  {Number(row?.gross_profit_percentage ?? 0).toFixed(2)}%
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="Quotation value">{formatCurrency(Number(row?.total_before_tax ?? lineTotals.totalBeforeTax))}</Descriptions.Item>
            <Descriptions.Item label="Before tax">{formatCurrency(lineTotals.totalBeforeTax)}</Descriptions.Item>
            <Descriptions.Item label="Tax">{formatCurrency(lineTotals.totalTax)}</Descriptions.Item>
            <Descriptions.Item label="Grand total">{formatCurrency(lineTotals.grandTotal)}</Descriptions.Item>
            <Descriptions.Item label="Related quotations">{relatedQuotations.length}</Descriptions.Item>
            <Descriptions.Item label="Stock insufficient lines">{Number((availabilitySummary as any)?.insufficient_stock_lines || 0)}</Descriptions.Item>
            <Descriptions.Item label="Rental unconfirmed lines">{Number((availabilitySummary as any)?.unconfirmed_rental_lines || 0)}</Descriptions.Item>
            <Descriptions.Item label="Rental date conflicts">{Number((availabilitySummary as any)?.conflicting_rental_lines || 0)}</Descriptions.Item>
          </Descriptions>

          {id && row?.approval_instance_id ? (
            <Card size="small" title="Approval workflow">
              <ApprovalPanel
                entityType={ENTITY_TYPE_ESTIMATION}
                entityId={id}
                onApproveViaDocument={async (remarks) => {
                  await tradingRentalEstimationService.approve(id, remarks);
                  message.success('Approval submitted');
                }}
                onRejectViaDocument={async (payload) => {
                  await tradingRentalEstimationService.rejectUniversalApproval(id, payload as any);
                  message.success('Rejected');
                }}
                extraInvalidateQueryKeys={[[...['trading-rental-estimations', 'detail', id]]]}
              />
            </Card>
          ) : null}

          {id ? (
            <Card size="small" title={`Related quotations (${relatedQuotations.length})`}>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={relatedQuotations}
                columns={[
                  {
                    title: 'Quotation',
                    key: 'quotation_number',
                    render: (_: unknown, record: any) => (
                      <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/quotations/${record.id}`)}>
                        {record.quotation_number}
                      </Button>
                    ),
                  },
                  { title: 'Date', dataIndex: 'quotation_date', render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—') },
                  { title: 'Status', dataIndex: 'status' },
                  {
                    title: 'Selected lines',
                    render: (_: unknown, record: any) =>
                      (record.items || []).filter((i: any) => Boolean(i.trading_rental_estimation_line_id)).length,
                  },
                  { title: 'Value', render: (_: unknown, record: any) => formatCurrency(Number(record.grand_total || 0)) },
                  {
                    title: '',
                    key: 'resync',
                    render: (_: unknown, record: any) =>
                      record.is_latest_revision !== false && !record.is_converted_to_sales_order ? (
                        <Button size="small" onClick={() => navigate(`/growth/quotations/${record.id}/source-sync`)}>
                          Resync quotation
                        </Button>
                      ) : null,
                  },
                ]}
              />
            </Card>
          ) : null}
        </Space>
      </Card>

      <Modal
        title={`${editingIndex != null ? 'Edit' : 'Add'} ${lineModalType} line`}
        open={lineModalOpen}
        onCancel={() => {
          setLineModalOpen(false);
          setEditingLineId(null);
        }}
        onOk={saveLine}
        width={900}
      >
        <Form
          form={lineForm}
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            if (
              Object.prototype.hasOwnProperty.call(changedValues, 'unit_cost') ||
              Object.prototype.hasOwnProperty.call(changedValues, 'selling_unit_price') ||
              Object.prototype.hasOwnProperty.call(changedValues, 'line_type')
            ) {
              syncMarginMarkup(allValues as Partial<TradingRentalEstimationLine>);
              return;
            }
            if (Object.prototype.hasOwnProperty.call(changedValues, 'margin_percentage')) {
              syncFromMargin(allValues as Partial<TradingRentalEstimationLine>);
              return;
            }
            if (Object.prototype.hasOwnProperty.call(changedValues, 'markup_percentage')) {
              syncFromMarkup(allValues as Partial<TradingRentalEstimationLine>);
            }
          }}
        >
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="line_type" label="Line type" initialValue={lineModalType} rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'Trading', label: 'Trading' },
                    { value: 'Rental', label: 'Rental' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="item_id" label="Inventory item">
                <ItemSelect
                  items={inventoryItems}
                  autoLoad={false}
                  loading={inventoryItemsLoading}
                  onChange={(v) => {
                    if (v) onPickItem(String(v));
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="item_code" label="Item code">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="item_name" label="Item name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="uom" label="UOM">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="item_description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="quantity" label="Quantity" initialValue={1} rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="unit_cost"
                label={lineModalType === 'Rental' ? 'Cost rate / period' : 'Unit cost'}
                initialValue={0}
              >
                <InputNumber min={0} style={{ width: '100%' }} disabled={!canEditCost} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="selling_unit_price" label="Selling unit price" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="tax_percentage" label="Tax %" initialValue={5}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="margin_percentage" label="Margin %" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} disabled={!canEditCost} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="markup_percentage" label="Markup %" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} disabled={!canEditCost} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="discount_percentage" label="Discount %" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="availability_status" label="Availability status">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Rental fields</Divider>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="rental_period_type" label="Period type">
                <Select
                  allowClear
                  options={[
                    { value: 'Daily', label: 'Daily' },
                    { value: 'Weekly', label: 'Weekly' },
                    { value: 'Monthly', label: 'Monthly' },
                    { value: 'Custom', label: 'Custom' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="rental_start_date" label="Rental start date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="rental_end_date" label="Rental end date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="rental_duration" label="Rental duration" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="rental_rate" label="Rental rate" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} disabled={!canEditCost} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="security_deposit" label="Security deposit" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="mobilization_charges" label="Mobilization" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="demobilization_charges" label="Demobilization" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="operator_charges" label="Operator charges" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="transport_charges" label="Transport charges" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="damage_charges" label="Damage charges" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <EntityAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_TRADING_RENTAL_ESTIMATION_LINE}
            entityId={editingLineId}
            title="Line attachments"
            readOnly={false}
            queryKeyPrefix={['documents', 'trading-rental-line']}
          />
        </Form>
      </Modal>

      <Modal
        title="Create Quotation from Trading/Rental Estimation"
        open={convertModalOpen}
        onCancel={() => setConvertModalOpen(false)}
        onOk={convertToQuotation}
        okText="Create quotation"
        okButtonProps={{ loading: convertMutation.isPending }}
        width={980}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Descriptions size="small" bordered column={2}>
            <Descriptions.Item label="Estimation">{row?.estimation_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="Type">{row?.estimation_type || '—'}</Descriptions.Item>
            <Descriptions.Item label="Customer">{row?.customer_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Opportunity">
              {rowSourceOpportunity
                ? `${rowSourceOpportunity.opportunity_number} — ${rowSourceOpportunity.opportunity_name}`
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">{row?.status || '—'}</Descriptions.Item>
            <Descriptions.Item label="Existing quotations">{relatedQuotations.length}</Descriptions.Item>
            <Descriptions.Item label="Selected lines">{selectedLineIds.length}</Descriptions.Item>
          </Descriptions>
          <div>
            <strong>
              Department <span style={{ color: '#ff4d4f' }}>*</span>
            </strong>
            <div style={{ marginTop: 8 }}>
              <DepartmentSelect
                value={convertDepartmentId}
                onChange={(value) => setConvertDepartmentId(value || undefined)}
                style={{ width: '100%' }}
                placeholder="Select department"
              />
            </div>
          </div>
          <Space>
            <Button onClick={() => setSelectedLineIds(lines.map((l, idx) => String(l.id || `tmp-${idx}`)))}>Select all</Button>
            <Button onClick={() => setSelectedLineIds([])}>Deselect all</Button>
          </Space>
          <Table
            size="small"
            rowKey={(_, idx) => String(lines[idx!]?.id || `tmp-${idx}`)}
            dataSource={lines}
            pagination={{ pageSize: 8 }}
            rowSelection={{
              selectedRowKeys: selectedLineIds,
              onChange: (keys) => setSelectedLineIds(keys.map((k) => String(k))),
            }}
            columns={[
              { title: '#', dataIndex: 'line_number', width: 70 },
              { title: 'Type', dataIndex: 'line_type' },
              { title: 'Item', dataIndex: 'item_name' },
              { title: 'Description', dataIndex: 'item_description' },
              { title: 'Qty', dataIndex: 'quantity' },
              { title: 'UOM', dataIndex: 'uom' },
              {
                title: 'Amount',
                render: (_: unknown, record: any) => formatCurrency(Number(record.selling_amount || 0)),
              },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title={`Import ${importLineType.toLowerCase()} lines`}
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={confirmImportLines}
        okText={`Import ${importLineType.toLowerCase()} lines`}
        okButtonProps={{
          loading: importBusy || importLinesMutation.isPending,
          disabled: !importPreview || importPreview.lines.length === 0 || importPreview.errors.length > 0,
        }}
        width={1100}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void tradingRentalEstimationService.downloadLinesImportTemplate(importLineType)}
            >
              Download {importLineType.toLowerCase()} template
            </Button>
            <Radio.Group value={importMode} onChange={(e) => setImportMode(e.target.value)}>
              <Radio value="replace">Replace existing {importLineType.toLowerCase()} lines</Radio>
              <Radio value="append">Append to existing lines</Radio>
            </Radio.Group>
          </Space>
          <Alert
            type="info"
            showIcon
            message={
              importLineType === 'Trading'
                ? 'Trading template: Item, Quantity, UOM, Unit Cost and Selling Unit Price (or Markup %), plus Discount %, VAT %, delivery date, supplier and remarks.'
                : 'Rental template: Item, Quantity, UOM, Rental Period Type, dates or duration, Cost Rate and Rental Rate per period, plus deposit, mobilization, demobilization, operator, transport and damage charges.'
            }
            description={
              importMode === 'replace'
                ? `Existing ${importLineType.toLowerCase()} lines will be replaced; ${importLineType === 'Trading' ? 'rental' : 'trading'} lines are kept.`
                : 'Imported lines are added after the existing lines.'
            }
          />
          <Upload.Dragger {...importUploadProps}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag the {importLineType.toLowerCase()} template (.xlsx, .csv)</p>
            <p className="ant-upload-hint">Columns marked * in the template are required.</p>
          </Upload.Dragger>
          {importPreview && importPreview.errors.length > 0 ? (
            <Alert
              type="error"
              showIcon
              message={`${importPreview.errors.length} error(s) — fix the file and upload again`}
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {importPreview.errors.slice(0, 20).map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              }
            />
          ) : null}
          {importPreview && importPreview.warnings.length > 0 ? (
            <Alert
              type="warning"
              showIcon
              message="Warnings"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {importPreview.warnings.slice(0, 20).map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              }
            />
          ) : null}
          {importPreview && importPreview.lines.length > 0 ? (
            <Table
              size="small"
              rowKey={(_, idx) => String(idx)}
              pagination={{ pageSize: 8 }}
              dataSource={importPreview.lines}
              scroll={{ x: true }}
              columns={[
                { title: 'Item code', dataIndex: 'item_code', width: 110 },
                { title: 'Item name', dataIndex: 'item_name' },
                { title: 'Qty', dataIndex: 'quantity', width: 70, align: 'right' as const },
                { title: 'UOM', dataIndex: 'uom', width: 70 },
                ...(importLineType === 'Rental'
                  ? [
                      { title: 'Period', dataIndex: 'rental_period_type', width: 80 },
                      { title: 'Duration', dataIndex: 'rental_duration', width: 80, align: 'right' as const },
                      {
                        title: 'Cost rate',
                        dataIndex: 'unit_cost',
                        width: 100,
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(Number(v || 0)),
                      },
                      {
                        title: 'Rental rate',
                        dataIndex: 'rental_rate',
                        width: 100,
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(Number(v || 0)),
                      },
                    ]
                  : [
                      {
                        title: 'Unit cost',
                        dataIndex: 'unit_cost',
                        width: 100,
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(Number(v || 0)),
                      },
                      {
                        title: 'Selling price',
                        dataIndex: 'selling_unit_price',
                        width: 110,
                        align: 'right' as const,
                        render: (v: number) => formatCurrency(Number(v || 0)),
                      },
                    ]),
                {
                  title: 'Cost',
                  dataIndex: 'cost_amount',
                  width: 110,
                  align: 'right' as const,
                  render: (v: number) => formatCurrency(Number(v || 0)),
                },
                {
                  title: 'Selling (excl. VAT)',
                  dataIndex: 'selling_amount',
                  width: 130,
                  align: 'right' as const,
                  render: (v: number) => formatCurrency(Number(v || 0)),
                },
                {
                  title: 'Margin %',
                  dataIndex: 'margin_percentage',
                  width: 90,
                  align: 'right' as const,
                  render: (v: number) => `${Number(v || 0).toFixed(2)}%`,
                },
              ]}
            />
          ) : null}
        </Space>
      </Modal>
    </div>
  );
};

export default TradingRentalEstimationBuilderPage;

